import { Button } from 'components/common/button/Button';
import './ScheduleMainContent.css';

interface Schedule {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
}

interface Participant {
    id: string;
    name: string;
    avatar: string;
    schedules: Schedule[];
}

interface TimeSlot {
    date: string;
    startTime: string;
    endTime: string;
    availableCount: number;
    voteCount: number;
    proposers: any[];
    voters: string[];
    isRecommended: boolean;
    hasUserVoted: boolean;
}

interface ScheduleMainContentProps {
    participants: Participant[];
    timeSlots: TimeSlot[];
    aiRecommendations: any[];
    onAddSchedule: () => void;
    onVote: () => void;
}

export const ScheduleMainContent = ({
    participants,
    timeSlots,
    aiRecommendations,
    onAddSchedule,
    onVote
}: ScheduleMainContentProps) => {
    // 일정을 제출한 참여자 수 계산
    const submittedCount = participants.filter(p => p.schedules && p.schedules.length > 0).length;
    const totalCount = participants.length;

    // 날짜 포맷팅 (YYYY-MM-DD → MM월 DD일)
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return `${date.getMonth() + 1}월 ${date.getDate()}일`;
    };

    // 요일 표시
    const getDayOfWeek = (dateString: string) => {
        const date = new Date(dateString);
        const days = ['일', '월', '화', '수', '목', '금', '토'];
        return days[date.getDay()];
    };

    console.log('ScheduleMainContent - aiRecommendations:', aiRecommendations);
    console.log('ScheduleMainContent - aiRecommendations.length:', aiRecommendations?.length);

    // 투표 수 기준으로 정렬 (많은 순서대로)
    const sortedTimeSlots = [...timeSlots].sort((a, b) => {
        // 투표 수로 먼저 정렬 (내림차순)
        if (b.voteCount !== a.voteCount) {
            return b.voteCount - a.voteCount;
        }
        // 투표 수가 같으면 제안 수로 정렬 (내림차순)
        if (b.availableCount !== a.availableCount) {
            return b.availableCount - a.availableCount;
        }
        // 그것도 같으면 날짜/시간 순서대로 (오름차순)
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.startTime.localeCompare(b.startTime);
    });

    return (
        <div className="schedule-main-content-wrapper">
            {/* 상단 진행 상황 카드 */}
            <div className="schedule-progress-card">
                <div className="progress-header">
                    <div className="progress-icon">📅</div>
                    <div className="progress-info">
                        <h2 className="progress-title">일정 조율 진행중</h2>
                        <p className="progress-description">
                            참여자들의 가능한 시간을 확인하고 있어요
                        </p>
                    </div>
                </div>

                <div className="progress-section">
                    <div className="progress-bar-container">
                        <div
                            className="progress-bar-fill"
                            style={{ width: totalCount > 0 ? `${(submittedCount / totalCount) * 100}%` : '0%' }}
                        />
                    </div>
                    <div className="progress-stats">
                        <span className="stats-count">{submittedCount}/{totalCount}명 입력 완료</span>
                        <span className="stats-percent">
                            {totalCount > 0 ? Math.round((submittedCount / totalCount) * 100) : 0}%
                        </span>
                    </div>
                </div>
            </div>

            {/* 투표 현황 및 버튼 */}
            {sortedTimeSlots && sortedTimeSlots.length > 0 && (
                <div className="vote-summary-section">
                    <div className="vote-summary-header">
                        <div className="vote-summary-info">
                            <h3 className="section-title">📊 투표 현황</h3>
                            <p className="vote-summary-text">
                                제안된 시간대에 투표해주세요
                            </p>
                        </div>
                    </div>
                    <div className="vote-summary-list">
                        {sortedTimeSlots.slice(0, 5).map((slot, index) => (
                            <div key={index} className="vote-summary-item">
                                <div className="vote-summary-time">
                                    {formatDate(slot.date)} ({getDayOfWeek(slot.date)}) {slot.startTime.substring(0, 5)} ~ {slot.endTime.substring(0, 5)}
                                </div>
                                <div className="vote-summary-count">
                                    {slot.isRecommended && <span className="recommend-badge-small">추천</span>}
                                    <span className="vote-count">👍 {slot.voteCount}표</span>
                                    <span className="proposer-count">({slot.availableCount}명 제안)</span>
                                </div>
                            </div>
                        ))}
                        {sortedTimeSlots.length > 5 && (
                            <div className="vote-summary-more" onClick={onVote}>
                                +{sortedTimeSlots.length - 5}개 시간대 더보기
                            </div>
                        )}
                    </div>
                    <div className="vote-summary-button-wrapper">
                        <Button
                            text="투표하기"
                            onPress={onVote}
                        />
                    </div>
                </div>
            )}

            {/* AI 추천 일정 */}
            {aiRecommendations && aiRecommendations.length > 0 && (
                <div className="ai-recommend-section">
                    <div className="section-header">
                        <h3 className="section-title">🤖 AI 추천 일정</h3>
                        <span className="section-subtitle">
                            가장 많은 참여자가 가능한 시간이에요
                        </span>
                    </div>
                    <div className="ai-recommend-cards">
                        {aiRecommendations.slice(0, 3).map((rec: any, index: number) => {
                            const startDate = new Date(rec.startDateTime);
                            const endDate = new Date(rec.endDateTime);
                            return (
                                <div key={index} className="ai-recommend-card">
                                    <div className="recommend-badge">추천 {index + 1}</div>
                                    <div className="recommend-date">
                                        {formatDate(startDate.toISOString().split('T')[0])} ({getDayOfWeek(startDate.toISOString().split('T')[0])})
                                    </div>
                                    <div className="recommend-time">
                                        {startDate.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} ~ {endDate.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                    <div className="recommend-count">
                                        {rec.availableCount}/{rec.totalCount}명 참여 가능
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 참여자별 일정 목록 */}
            <div className="participants-schedule-section">
                <div className="section-header">
                    <h3 className="section-title">✨ 참여자별 가능 시간</h3>
                    <span className="section-subtitle">
                        {submittedCount}명이 입력을 완료했어요
                    </span>
                </div>

                <div className="participants-list">
                    {/* 입력 완료한 참여자들 */}
                    {participants
                        .filter(p => p.schedules && p.schedules.length > 0)
                        .map((participant) => (
                            <div key={participant.id} className="participant-group">
                                <div className="participant-header">
                                    <div className="participant-avatar-large">{participant.avatar}</div>
                                    <div className="participant-details">
                                        <div className="participant-name">{participant.name}</div>
                                        <div className="participant-status submitted">
                                            ✓ {participant.schedules.length}개 입력 완료
                                        </div>
                                    </div>
                                </div>

                                <div className="schedule-cards-container">
                                    {participant.schedules.map((schedule) => (
                                        <div key={schedule.id} className="schedule-card">
                                            <div className="schedule-card-icon">📅</div>
                                            <div className="schedule-card-content">
                                                <div className="schedule-card-date">
                                                    {formatDate(schedule.date)} ({getDayOfWeek(schedule.date)})
                                                </div>
                                                <div className="schedule-card-time">
                                                    {schedule.startTime} ~ {schedule.endTime}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                    {/* 입력 대기중인 참여자들 - 하나로 묶어서 표시 */}
                    {totalCount - submittedCount > 0 && (
                        <div className="participant-group pending-group">
                            <div className="participant-header">
                                <div className="participant-avatar-large pending-avatar">⏳</div>
                                <div className="participant-details">
                                    <div className="participant-name">{totalCount - submittedCount}명 입력 대기중</div>
                                    <div className="participant-status pending">
                                        {participants
                                            .filter(p => !p.schedules || p.schedules.length === 0)
                                            .map(p => p.name)
                                            .join(', ')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 안내 메시지 */}
            <div className="schedule-info-box">
                <div className="info-icon">💡</div>
                <div className="info-text">
                    모든 참여자가 입력을 완료하면 최적의 시간을 알려드릴게요
                </div>
            </div>

            {/* 하단 액션 버튼 */}
            <div className="schedule-action-footer">
                <Button
                    text="내 일정 입력하기"
                    onPress={onAddSchedule}
                />
            </div>
        </div>
    );
};
